// The actions a player can take, grouped as on the rulebook's player aid.
// label is the short name shown in the action panel; title is the rulebook's full name.
// generate(gameState, player) lists candidate moves with a label and cost; the engine keeps only the legal ones.
// Costs leave out the usual 1 civil / military action; only unusual action costs are spelled out.
// costNote: how cards changed the usual cost, e.g. "−1 Moses" (see Costs.js).
// Categories without generate are not implemented yet.
// onBoard: taken by clicking the board (e.g. the card row) rather than from the action panel.
// single: the category is one action, so the panel shows just its button without a category name.
// prompt: shown as one button with this text that opens a choice of targets (for rarely used actions with many targets).
// choiceOf: { id, label } on options: options sharing it are one button with that label, opening a choice between them
// when there is more than one (e.g. an action card and the buildings it could be used on).
const { Phase, MoveTypes } = require('./MoveTypes');
const { CivilCardTypes, MilitaryCardTypes } = require('../Constants/Names/CardTypes');
const TechnologyTypes = require('../Constants/TechnologyTypes');
const { SwappedPart, GetTakeCardCostDetails, GetPopulationCost, GetBuildCost, GetUpgradeCost, GetDevelopCost, AdjustmentText,
    WonderStagesPerAction, GetWonderStagesCost, WithCardDiscount } = require('./Costs');

const { Theater } = TechnologyTypes.Urban;
const isUrbanType = (type) => Object.values(TechnologyTypes.Urban).includes(type);
const ActionEffects = require('../Constants/Actions/ActionEffects');
const LeaderNames = require('../Constants/Names/LeaderNames');
const WonderNames = require('../Constants/Names/WonderNames');
const { CostKinds, HasRuleException, Rules } = require('../Effects/Effects');
const { AgeOrder } = require('../Constants/Constants');

const ActionGroups = {
    Political: "Political",
    Civil: "Civil",
    Military: "Military",
}

// "1 resource", "2 resources"
const resources = (count) => `${count} resource${count === 1 ? "" : "s"}`;

// An option's cost and note from a cost (see Costs.MakeCost), with format giving the text for the total
// The cost as shown: the total, less any part paid in another currency (Trade Routes Agreement), which is added after
// it, e.g. "2 resources, 1 food"
const costFields = (cost, format) => {
    const swapped = Object.entries(SwappedPart(cost));
    const moved = swapped.reduce((sum, [, amount]) => sum + amount, 0);
    const label = [format(cost.total - moved), ...swapped.map(([currency, amount]) => `${amount} ${currency}`)].join(', ');
    return cost.adjustments.length ? { cost: label, costNote: AdjustmentText(cost) } : { cost: label };
};

const isMilitaryType = (type) => Object.values(TechnologyTypes.Military).includes(type);
const isBuildableCivil = (tech) => tech.workerCost !== null && tech.workerCost !== undefined && !isMilitaryType(tech.type);
const isBuildableMilitary = (tech) => tech.workerCost !== null && tech.workerCost !== undefined && isMilitaryType(tech.type);

// Every move of a worker from a card to a higher-level card of the same type
const upgradeOptions = (player, isKind) => player.technologies
    .filter(isKind)
    .filter(from => from.GetWorkerCount() > 0)
    .flatMap(from => player.technologies
        .filter(to => isKind(to) && to.type === from.type && AgeOrder.indexOf(to.age) > AgeOrder.indexOf(from.age))
        .map(to => ({
            label: `${from.name} → ${to.name}`,
            ...costFields(GetUpgradeCost(player, from, to), resources),
            move: { type: MoveTypes.Upgrade, fromTech: from.name, toTech: to.name },
        })));

// Every card with a worker on it
const destroyOptions = (player, isKind) => player.technologies
    .filter(tech => isKind(tech) && tech.GetWorkerCount() > 0)
    .map(tech => ({ label: tech.name, move: { type: MoveTypes.Destroy, techName: tech.name } }));

const ActionCategories = [
    // Politics phase: at most one of these, or pass
    {
        id: "prepareEvent",
        label: "Prepare Event",
        title: "Prepare an Event",
        group: ActionGroups.Political,
        generate: (gameState, player) => player.militaryHand
            // (any military card with Confucius)
            .filter(card => card.type === MilitaryCardTypes.Event || card.type === MilitaryCardTypes.Territory || HasRuleException(player, Rules.PrepareAnyMilitaryCard))
            .map(card => ({
                label: card.name,
                cost: `+${AgeOrder.indexOf(card.age)} culture`,
                move: { type: MoveTypes.PrepareEvent, cardId: card.id },
            })),
    },
    {
        id: "alexander",
        label: "Alexander",
        title: "Alexander the Great: remove him from play to add 1 yellow token from the box to your yellow bank",
        group: ActionGroups.Political,
        single: true,
        generate: (gameState, player) => player.leader?.name !== LeaderNames.AlexanderTheGreat ? []
            : [{ label: "Retire Alexander: +1 yellow token", move: { type: MoveTypes.AlexanderRetire } }],
    },
    {
        id: "redCross",
        label: "Red Cross",
        title: "International Red Cross of another player: build a stage (once a turn) for its food and a civil action, scoring 6 culture",
        group: ActionGroups.Civil,
        single: true,
        generate: (gameState, player) => gameState.playerStates
            .filter(owner => owner !== player && owner.wonderInProgress?.card.name === 'International Red Cross')
            .map(owner => ({ label: `${owner.name}'s Red Cross: a stage, +6 culture`, cost: `${owner.wonderInProgress.card.stageCosts[owner.wonderInProgress.stagesBuilt]} food`,
                move: { type: MoveTypes.RedCrossStage, owner: owner.playerNumber } })),
    },
    {
        id: "louvre",
        label: "Louvre Museum",
        title: "Louvre Museum: spend one of its blue tokens for 2 resources (no action)",
        group: ActionGroups.Civil,
        single: true,
        generate: (gameState, player) => player.wonders.some(w => w.name === WonderNames.LouvreMuseum && !w.crumbled) && player.CardState(WonderNames.LouvreMuseum).tokens > 0
            // ("1 blue token" and "2 resources" shown as their icons)
            ? [{ label: `Louvre Museum: 1 blue token → 2 resources (${player.CardState(WonderNames.LouvreMuseum).tokens} left)`, cost: "free", move: { type: MoveTypes.LouvreSpend } }] : [],
    },
    {
        id: "louvrePolitical",
        label: "Louvre Museum",
        title: "Louvre Museum: spend one of its blue tokens for 2 resources (no action, at any time)",
        group: ActionGroups.Political,
        single: true,
        generate: (gameState, player) => player.wonders.some(w => w.name === WonderNames.LouvreMuseum && !w.crumbled) && player.CardState(WonderNames.LouvreMuseum).tokens > 0
            ? [{ label: `Louvre Museum: 1 blue token → 2 resources (${player.CardState(WonderNames.LouvreMuseum).tokens} left)`, cost: "free", move: { type: MoveTypes.LouvreSpend } }] : [],
    },
    {
        id: "coubertin",
        label: "Olympic Games",
        title: "Pierre de Coubertin, once per game, before your political action: +8 culture; until your next turn, whoever plays a war or aggression loses 8 culture",
        group: ActionGroups.Political,
        single: true,
        generate: (gameState, player) => player.leader?.name !== LeaderNames.PierreDeCoubertin || player.CardState(LeaderNames.PierreDeCoubertin).used ? []
            : [{ label: "Declare the Olympic Games: +8 culture", move: { type: MoveTypes.CoubertinOlympics } }],
    },
    {
        id: "catherine",
        label: "Catherine",
        title: "Catherine the Great, once per game: each civilization weaker than you loses 1 yellow token; if at least one does (two in a 4-player game), you gain 1",
        group: ActionGroups.Political,
        single: true,
        generate: (gameState, player) => player.leader?.name !== LeaderNames.CatherineTheGreat || player.CardState(LeaderNames.CatherineTheGreat).used ? []
            : [{ label: "Decree: weaker civilizations lose 1 yellow token", move: { type: MoveTypes.CatherineDecree } }],
    },
    {
        id: "columbus",
        label: "Columbus",
        title: "Christopher Columbus: remove him from play to colonize a territory in your hand, with no sacrifice",
        group: ActionGroups.Political,
        generate: (gameState, player) => player.leader?.name !== LeaderNames.ChristopherColumbus ? [] : player.militaryHand
            .filter(card => card.type === MilitaryCardTypes.Territory)
            .map(card => ({ label: `Colonize ${card.name}`, move: { type: MoveTypes.ColumbusColonize, cardId: card.id } })),
    },
    {
        id: "playAggression",
        label: "Aggression",
        title: "Play an Aggression against a weaker rival",
        group: ActionGroups.Political,
        // Shown as one button per card, choosing the rival with their military in view (see the client's AggressionPicker)
        ui: 'aggression',
        generate: (gameState, player) => player.militaryHand
            .filter(card => card.type === MilitaryCardTypes.Aggression)
            .flatMap(card => gameState.playerStates.filter(p => p !== player && !p.resigned).flatMap(target => [null, ...require('./Aggression').HimejiUnits(player)]
                // (with Himeji Castle: also with each unit it could add, chosen in the picker)
                .map(unit => ({
                    label: target.name,
                    choiceOf: { id: card.id, label: card.name },
                    ...(unit ? { himejiUnit: unit.name } : {}),
                    move: { type: MoveTypes.PlayAggression, cardId: card.id, target: target.playerNumber, ...(unit ? { himejiUnit: unit.name } : {}) },
                })))),
    },
    {
        id: "declareWar",
        label: "War",
        title: "Declare a war on a rival, resolved at the start of your next turn",
        group: ActionGroups.Political,
        // One button per card, choosing the rival with their strength and the cost in view (see the client's AggressionPicker)
        ui: 'war',
        generate: (gameState, player) => player.militaryHand
            .filter(card => card.type === MilitaryCardTypes.War)
            .flatMap(card => gameState.playerStates.filter(p => p !== player && !p.resigned).map(target => ({
                label: target.name,
                choiceOf: { id: card.id, label: card.name },
                move: { type: MoveTypes.DeclareWar, cardId: card.id, target: target.playerNumber },
            }))),
    },
    {
        id: "offerPact",
        label: "Pact",
        title: "Offer a pact to a rival",
        group: ActionGroups.Political,
        // One button per card, choosing the rival and the roles with what each side gets (see the client's PactPicker)
        ui: 'pact',
        generate: (gameState, player) => player.militaryHand
            .filter(card => card.type === MilitaryCardTypes.Pact)
            .flatMap(card => gameState.playerStates.filter(p => p !== player && !p.resigned).flatMap(target =>
                (require('./Pact').IsOneSided(card.name) ? [true, false] : [true]).map(offererIsA => ({
                    label: target.name,
                    choiceOf: { id: card.id, label: card.name },
                    move: { type: MoveTypes.OfferPact, cardId: card.id, target: target.playerNumber, offererIsA },
                })))),
    },
    {
        id: "cancelPact",
        label: "Cancel pact",
        title: "Cancel a pact you are in",
        group: ActionGroups.Political,
        generate: (gameState, player) => require('./Pact').PactsOf(gameState, player.playerNumber).map(pact => ({
            label: `${pact.card.name} with ${gameState.playerStates[pact.a === player.playerNumber ? pact.b : pact.a].name}`,
            move: { type: MoveTypes.CancelPact, cardId: pact.card.id },
        })),
    },

    // Action phase: civil actions
    {
        id: "takeCard",
        label: "Take Card",
        title: "Take a Card from the Card Row",
        group: ActionGroups.Civil,
        onBoard: true,
        generate: (gameState, player) => gameState.civilCardRow
            .map((card, slot) => card && {
                label: card.name,
                ...costFields(GetTakeCardCostDetails(player, card, slot), total => `${total} CA`),
                move: { type: MoveTypes.TakeCard, cardId: card.id },
            })
            .filter(option => option),
    },
    {
        id: "increasePopulation",
        label: "Increase Population",
        title: "Increase Population",
        group: ActionGroups.Civil,
        single: true,
        generate: (gameState, player) => {
            const cost = GetPopulationCost(player, player.poolChoice);
            return [{
                label: "Increase Population",
                ...(cost ? costFields(cost, total => `${total} food`) : {}),
                move: { type: MoveTypes.IncreasePopulation },
            }];
        },
    },
    {
        id: "build",
        label: "Build",
        title: "Build a Farm, Mine, or Urban Building",
        group: ActionGroups.Civil,
        generate: (gameState, player) => player.technologies
            .filter(isBuildableCivil)
            .map(tech => ({
                label: tech.name,
                ...costFields(GetBuildCost(player, tech), resources),
                move: { type: MoveTypes.Build, techName: tech.name },
            })),
    },
    {
        id: "oceanLiner",
        label: "Ocean Liner",
        title: "Ocean Liner: gain 1 population, once per turn, for no civil action or food",
        group: ActionGroups.Civil,
        single: true,
        generate: () => [{ label: "Increase Population (Ocean Liner)", cost: "free", move: { type: MoveTypes.OceanLinerPopulation } }],
    },
    {
        id: "upgrade",
        label: "Upgrade",
        title: "Upgrade a Farm, Mine, or Urban Building",
        group: ActionGroups.Civil,
        generate: (gameState, player) => upgradeOptions(player, isBuildableCivil),
    },
    {
        id: "playLeader",
        label: "Play Leader",
        title: "Play a Leader",
        group: ActionGroups.Civil,
        generate: (gameState, player) => player.civilHand
            .filter(card => card.type === CivilCardTypes.Leader)
            .flatMap(card => player.leader?.name === LeaderNames.Homer && player.wonders.length > 0
                // Replacing Homer: keep the returned civil action, or give it up for a happy face on a completed wonder
                ? [
                    { label: "Keep the civil action", move: { type: MoveTypes.PlayLeader, cardId: card.id } },
                    ...player.wonders.map(wonder => ({
                        label: `+1 happy face on ${wonder.name}`,
                        cost: "1 CA",
                        move: { type: MoveTypes.PlayLeader, cardId: card.id, homerWonderId: wonder.id },
                    })),
                ].map(option => ({ ...option, choiceOf: { id: card.id, label: `${card.name} (replaces Homer)` } }))
                : player.leader?.name === LeaderNames.Hammurabi
                // Replacing Hammurabi: pay with a civil action as usual, or with a military action (his ability)
                ? [
                    { label: card.name, cost: "replace with CA", move: { type: MoveTypes.PlayLeader, cardId: card.id } },
                    { label: card.name, cost: "replace with MA", move: { type: MoveTypes.PlayLeader, cardId: card.id, payWithMilitary: true } },
                ]
                : [{
                    label: card.name,
                    cost: player.leader ? `replaces ${player.leader.name}` : undefined,
                    move: { type: MoveTypes.PlayLeader, cardId: card.id },
                }]),
    },
    {
        id: "buildWonder",
        label: "Build Wonder",
        title: "Build a Stage of a Wonder",
        group: ActionGroups.Civil,
        // One option per number of stages that can be built for one action (more with construction technologies)
        generate: (gameState, player) => {
            const wonder = player.wonderInProgress;
            if (wonder === null)
            {
                return [];
            }
            const remaining = wonder.card.stageCosts.length - wonder.stagesBuilt;
            return Array.from({ length: Math.min(remaining, WonderStagesPerAction(player)) }, (_, index) => index + 1)
                .map(stages => ({
                    label: stages === 1 ? `${wonder.card.name}` : `${wonder.card.name} × ${stages} stages`,
                    // (in food for the International Red Cross)
                    ...costFields(GetWonderStagesCost(player, stages), require('./Costs').WonderCurrency(wonder) === 'food' ? (total => `${total} food`) : resources),
                    move: { type: MoveTypes.BuildWonderStage, stages },
                }));
        },
    },
    {
        id: "developTechnology",
        label: "Develop",
        title: "Develop a Technology",
        group: ActionGroups.Civil,
        generate: (gameState, player) => player.civilHand
            .filter(card => card.type === CivilCardTypes.Technology && card.Technology.type !== TechnologyTypes.Government)
            .map(card => ({
                label: card.name,
                ...costFields(GetDevelopCost(player, card.Technology), total => `${total} science`),
                move: { type: MoveTypes.DiscoverTechnology, cardId: card.id },
            })),
    },
    {
        id: "bachUpgrade",
        label: "Bach",
        title: "Bach, once per turn: upgrade any urban building to a theater of the same level or higher, for the usual cost",
        group: ActionGroups.Civil,
        single: true,
        // One button per theater; it opens a choice of the building to turn into it
        generate: (gameState, player) => player.leader?.name !== LeaderNames.JohannesSebastianBach ? [] : player.technologies
            .filter(theater => theater.type === Theater)
            .flatMap(theater => player.technologies
                .filter(from => isUrbanType(from.type) && from.type !== Theater && from.GetWorkerCount() > 0)
                .map(from => ({
                    label: from.name,
                    ...costFields(GetUpgradeCost(player, from, theater), resources),
                    choiceOf: { id: `bach ${theater.name}`, label: `Upgrade to ${theater.name} (Bach)` },
                    move: { type: MoveTypes.BachUpgrade, fromTech: from.name, toTech: theater.name },
                }))),
    },
    {
        id: "churchill",
        label: "Churchill",
        title: "Winston Churchill: take 3 science and 3 resources for military units and their technologies this turn, instead of scoring 3 culture at the end of it",
        group: ActionGroups.Civil,
        single: true,
        generate: () => [{ label: "Churchill: +3 science, +3 resources for military", cost: "free", move: { type: MoveTypes.ChurchillTake } }],
    },
    {
        id: "changeGovernment",
        label: "Government",
        title: "Change your government: peacefully for its science cost and a civil action, or by a revolution for its lower revolution cost and all of your civil actions",
        group: ActionGroups.Civil,
        generate: (gameState, player) => {
            const militaryRevolution = player.ruleExceptionDelegate.some(rule => rule.rule === 'revolutionWithMilitaryActions');
            return player.civilHand
                .filter(card => card.Technology?.type === TechnologyTypes.Government)
                .flatMap(card => [
                    // (peacefully: as developing it, with what cards take off, e.g. the Acropolis)
                    { label: card.name, ...costFields(GetDevelopCost(player, card.Technology), total => `${total} science`),
                        move: { type: MoveTypes.ChangeGovernment, cardId: card.id, revolution: false } },
                    { label: `${card.name} (revolution)`, cost: `${card.Technology.revolutionCost} science, all ${militaryRevolution ? 'MA' : 'CA'}`,
                        move: { type: MoveTypes.ChangeGovernment, cardId: card.id, revolution: true } },
                ]);
        },
    },
    {
        id: "playAction",
        label: "Play Action",
        title: "Play an Action Card",
        group: ActionGroups.Civil,
        // One button per action card in hand; a card with several possible uses (targets, or a choice) opens a choice
        generate: (gameState, player) => player.civilHand
            .filter(card => card.type === CivilCardTypes.Action && ActionEffects[card.name])
            .flatMap(card => actionCardOptions(gameState, player, card)
                .map(option => ({ ...option, choiceOf: { id: card.id, label: card.name } }))),
    },

    // Action phase: military actions
    {
        id: "buildMilitary",
        label: "Build",
        title: "Build a Military Unit",
        group: ActionGroups.Military,
        generate: (gameState, player) => player.technologies
            .filter(isBuildableMilitary)
            .map(tech => ({
                label: tech.name,
                ...costFields(GetBuildCost(player, tech), resources),
                move: { type: MoveTypes.Build, techName: tech.name },
            })),
    },
    {
        id: "barbarossa",
        label: "Barbarossa",
        title: "Frederick Barbarossa: for 1 military action, increase population and build a military unit with it, each for 1 less",
        group: ActionGroups.Military,
        generate: (gameState, player) => player.leader?.name !== LeaderNames.FrederickBarbarossa ? [] : player.technologies
            .filter(isBuildableMilitary)
            .map(tech => {
                const source = LeaderNames.FrederickBarbarossa;
                const population = WithCardDiscount(player, { source, kind: CostKinds.PopulationFood, amount: 1 }, () => GetPopulationCost(player));
                const unit = WithCardDiscount(player, { source, kind: CostKinds.BuildPayment, amount: 1 }, () => GetBuildCost(player, tech));
                return {
                    label: tech.name,
                    cost: population ? `${population.total} food, ${resources(unit.total)}` : undefined,
                    move: { type: MoveTypes.BarbarossaRecruit, techName: tech.name },
                };
            }),
    },
    {
        id: "upgradeMilitary",
        label: "Upgrade",
        title: "Upgrade a Military Unit",
        group: ActionGroups.Military,
        generate: (gameState, player) => upgradeOptions(player, isBuildableMilitary),
    },
    {
        // A farm, mine or urban building (1 civil action) or a military unit (1 military action), picked on the board;
        // the action it spends is said when it is picked (spends). Last of the actions.
        id: "destroy",
        label: "Destroy",
        title: "Destroy a farm, mine or urban building (1 civil action), or a military unit (1 military action)",
        group: ActionGroups.Military,
        prompt: "Destroy",
        // (in a row of its own, below the groups: it is civil or military by what is picked)
        neutral: true,
        ui: 'boardPick',
        generate: (gameState, player) => [
            ...destroyOptions(player, isBuildableCivil).map(option => ({ ...option, spends: '1 CA' })),
            ...destroyOptions(player, isBuildableMilitary).map(option => ({ ...option, spends: '1 MA' })),
        ],
    },
    {
        id: "playTactic",
        label: "Play Tactic",
        title: "Play a Tactic",
        group: ActionGroups.Military,
        generate: (gameState, player) => player.militaryHand
            .filter(card => card.type === MilitaryCardTypes.Tactic)
            .map(card => ({ label: card.name, move: { type: MoveTypes.PlayTactic, cardId: card.id } })),
    },
    {
        id: "copyTactic",
        label: "Copy Tactic",
        title: "Copy a Tactic",
        group: ActionGroups.Military,
        // Copying costs 2 military actions, so that is spelled out
        // (copies of a tactic are one option, except a card that has gained strength (Sun Tzu), which is its own)
        generate: (gameState) => {
            const plain = [...new Set(gameState.publicTactics.filter(card => !gameState.tacticBonuses[card.id]).map(card => card.name))]
                .map(name => ({ label: name, cost: "2 MA", move: { type: MoveTypes.CopyTactic, tacticName: name } }));
            const boosted = gameState.publicTactics.filter(card => gameState.tacticBonuses[card.id])
                .map(card => ({ label: `${card.name} +${gameState.tacticBonuses[card.id]}`, cost: "2 MA",
                    move: { type: MoveTypes.CopyTactic, tacticName: card.name, tacticCardId: card.id } }));
            return [...plain, ...boosted];
        },
    },
];

// The ways an action card can be played: one per target of the action that comes with it (with its cost after the
// card's discount), one per choice, or just the card
function actionCardOptions(gameState, player, card)
{
    const effect = ActionEffects[card.name];
    const play = (extra) => ({ type: MoveTypes.PlayActionCard, cardId: card.id, ...extra });
    if (effect.choices)
    {
        return effect.choices(card, player).map(choice => ({ label: choice.label, move: play({ choice: choice.value }) }));
    }
    if (!effect.inner)
    {
        return [{ label: card.name, move: play() }];
    }
    // The targets come from the categories for those actions, worked out with the card's discount in place
    const found = effect.discount?.(card, player);
    const discount = found ? { source: card.name, kind: found.kind, amount: found.amount } : null;
    const categories = { [MoveTypes.Build]: ["build", "buildMilitary"], [MoveTypes.Upgrade]: ["upgrade", "upgradeMilitary"],
        [MoveTypes.DiscoverTechnology]: ["developTechnology"], [MoveTypes.ChangeGovernment]: ["changeGovernment"],
        [MoveTypes.IncreasePopulation]: ["increasePopulation"],
        [MoveTypes.BuildWonderStage]: ["buildWonder"] };
    return WithCardDiscount(player, discount, () => effect.inner.moves
        .flatMap(type => categories[type].flatMap(id => ActionCategories.find(category => category.id === id).generate(gameState, player)))
        .filter(option => {
            const tech = player.GetTechnology(option.move.techName ?? option.move.fromTech);
            return (!effect.inner.techTypes || (tech && effect.inner.techTypes.includes(tech.type)))
                && (effect.inner.stages === undefined || option.move.stages === effect.inner.stages);
        })
        .map(option => ({ ...option, costNote: withoutSource(option.costNote, card.name), move: play({ inner: option.move }) })));
}

// A cost note without one source's part, e.g. "−1 Urban Growth, −1 Masonry" without Urban Growth: "−1 Masonry"
function withoutSource(costNote, source)
{
    const parts = (costNote ?? '').split(', ').filter(part => part && part.replace(/^[+−]\d+ /, '') !== source);
    return parts.length ? parts.join(', ') : undefined;
}

const GroupPhase = {
    [ActionGroups.Political]: Phase.Political,
    [ActionGroups.Civil]: Phase.Action,
    [ActionGroups.Military]: Phase.Action,
}

module.exports = {
    ActionGroups,
    ActionCategories,
    GroupPhase,
}